import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { apiV2, type V2EscalationPolicy, type EnrichedMonitor, type V2UpdateMonitorBody } from '@/lib/api-v2-client'
import { AreaList } from '@/components/area-list'
import { edgeAreaSettings, edgeSupports, hasEnoughAreas, MIN_AREAS, savedAreas, savedSelection, type AreaSelection } from '@/lib/edge-areas'
import { editMonitorSchema, buildBrowserSettings, checkIntervalOptions, fastSlotLimit, timeoutMax, getFieldError, HTTP_METHODS, DNS_RECORD_TYPES, DNS_PLACEHOLDER_HINTS, DNS_VALUE_VALIDATORS, DNS_RECORD_CHANGE_TYPES, parsePortsSpec, formatPortsSpec, parseRecordList, formatRecordList, type PortSpec, type HttpMethod, type MonitorType, type BrowserEngine } from '@/lib/forms'
import { BrowserFields } from '@/components/browser-fields'
import { ThirdPartyEditForm } from '@/components/third-party/edit-form'
import { MonitorTypeBadge } from '@/components/monitor-type-badge'
import { isEditableMonitorType } from '@/lib/forms'
import { useAuth } from '@/hooks/use-auth'
import { useFreePlan } from '@/hooks/use-free-plan'
import { isFreeMonitorType } from '@/lib/free-plan'
import { isFreeTier } from '@/lib/subscription-access'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

interface Assertion {
  type: 'json_path' | 'regex' | 'header' | 'status_code' | 'contains' | 'not_contains'
  target: string
  operator: string
  value: string
}

const ASSERTION_TYPES = [
  { value: 'json_path', label: 'JSONPath' },
  { value: 'contains', label: 'Body contains' },
  { value: 'not_contains', label: 'Body not contains' },
  { value: 'regex', label: 'Regex match' },
  { value: 'header', label: 'Response header' },
  { value: 'status_code', label: 'Status code' },
] as const

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
] as const

function parseHeadersString(str: string): Record<string, string> {
  const headers: Record<string, string> = {}
  if (!str.trim()) return headers
  for (const line of str.split('\n')) {
    const idx = line.indexOf(':')
    if (idx > 0) {
      headers[line.slice(0, idx).trim()] = line.slice(idx + 1).trim()
    }
  }
  return headers
}

function headersToString(headers: Record<string, string> | undefined): string {
  if (!headers || Object.keys(headers).length === 0) return ''
  return Object.entries(headers)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n')
}

export const Route = createFileRoute('/_authenticated/dashboard/monitors/$id_/edit')({
  component: EditMonitorPage,
})

function EditMonitorPage() {
  const { id } = Route.useParams()

  const { data: monitorResponse, isLoading } = useQuery({
    queryKey: ['monitor', id],
    queryFn: () => apiV2.getMonitor(id),
  })

  const monitor = monitorResponse?.data

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-96 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (!monitor) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Monitor not found</p>
        <Button asChild className="mt-4">
          <Link to="/dashboard/monitors">Back to Monitors</Link>
        </Button>
      </div>
    )
  }

  if (monitor.monitor_type === 'third_party') {
    return <ThirdPartyEditForm key={monitor.id} monitor={monitor} />
  }

  // A type this build does not know has no edit fields here. Editing it with the HTTP
  // fields would send settings that belong to another type.
  if (!isEditableMonitorType(monitor.monitor_type)) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-8">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">{monitor.name}</h1>
          <MonitorTypeBadge type={monitor.monitor_type} />
        </div>
        <p className="text-body">
          This monitor type cannot be edited in this version of the app. You can still pause, resume or delete it from
          its page.
        </p>
        <Button asChild>
          <Link to="/dashboard/monitors/$id" params={{ id: monitor.id }}>Back to the monitor</Link>
        </Button>
      </div>
    )
  }

  return <MonitorEditForm key={monitor.id} monitor={monitor} />
}

function MonitorEditForm({ monitor }: { monitor: EnrichedMonitor }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // A free monitor is checked from one fixed location; its interval is the user's choice (owner, 2026-10-19).
  const { organization } = useAuth()
  const freePlan = useFreePlan()
  const freeMonitor = isFreeTier(organization) && isFreeMonitorType(monitor.monitor_type)
  // No subscription: 10 monitors may run at 30 s. A monitor already at 30 s keeps its slot;
  // moving another one to 30 s needs a free slot (the server enforces it, this only informs).
  const slotLimit = fastSlotLimit(organization?.plan, isFreeTier(organization))
  const { data: allMonitors } = useQuery({
    queryKey: ['monitors'],
    queryFn: () => apiV2.listMonitors({ per_page: 100 }),
    staleTime: 30_000,
    enabled: Number.isFinite(slotLimit),
  })
  const usedFastSlots = allMonitors?.data?.filter((m) => m.interval === 30).length ?? 0
  const fastBlocked = Number.isFinite(slotLimit) && monitor.interval !== 30 && usedFastSlots >= slotLimit

  const { data: policiesResponse } = useQuery({
    queryKey: ['escalation-policies'],
    queryFn: () => apiV2.listEscalationPolicies(),
    // Escalation policies are Pro: the server refuses the list for the free plan.
    enabled: !isFreeTier(organization),
  })

  const policies: V2EscalationPolicy[] = policiesResponse?.data ?? []

  const { data: alertChannelsData } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiV2.listAlertChannels(),
  })

  const alertChannels = alertChannelsData?.data ?? []

  // The channel ids this monitor alerts. null means ALL channels (the server saves that as []).
  // An empty list here means "none", and it cannot be saved. Never build the checked state from
  // "id !== false": an id that is not in the list would show as checked.
  const [chosen, setChosen] = useState<string[] | null>(() => {
    const saved = (monitor.alert_contacts ?? []) as string[]
    return saved.length === 0 ? null : saved
  })
  const isChannelChecked = (id: string) => chosen === null || chosen.includes(id)
  const noChannelChecked = alertChannels.length > 0 && !alertChannels.some((ch) => isChannelChecked(ch.id))

  function toggleChannel(id: string, checked: boolean) {
    // The first change turns "all" into an explicit list of every channel.
    const base = chosen ?? alertChannels.map((ch) => ch.id)
    setChosen(checked ? [...new Set([...base, id])] : base.filter((c) => c !== id))
  }

  const updateMutation = useMutation({
    mutationFn: (data: V2UpdateMonitorBody) => apiV2.updateMonitor(monitor.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      queryClient.invalidateQueries({ queryKey: ['monitor', monitor.id] })
      toast.success('Monitor updated')
      navigate({ to: '/dashboard/monitors/$id', params: { id: monitor.id } })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update monitor')
    },
  })

  const settings = (monitor.settings ?? {}) as Record<string, unknown>
  const { data: edgeInfoData } = useQuery({
    queryKey: ['edge-areas'],
    queryFn: () => apiV2.getEdgeAreas(),
    staleTime: 5 * 60_000,
    enabled: !freeMonitor && edgeSupports(monitor.monitor_type),
  })
  const edgeInfo = edgeInfoData?.data
  const showAreas = !freeMonitor && edgeSupports(monitor.monitor_type) && !!edgeInfo && edgeInfo.cap > 0
  // Null until the customer changes the list: the saved choice is shown and nothing is sent.
  const [areaChoice, setAreaChoice] = useState<AreaSelection | null>(null)
  const selection = areaChoice ?? (edgeInfo ? savedSelection(edgeInfo.areas, settings) : null)
  const [assertions, setAssertions] = useState<Assertion[]>(
    (settings.assertions as Assertion[]) || []
  )
  const currentMethod = (settings.method as string) || 'GET'
  const currentHeaders = settings.headers as Record<string, string> | undefined
  const currentBody = (settings.body as string) || ''
  // Types with their own edit fields keep their type; the rest (tcp, ping,
  // keyword) edit with the HTTP fields, as before.
  const EDIT_TYPES = ['dns', 'ssl', 'heartbeat', 'browser', 'domain', 'port_scan', 'dns_record', 'blocklist', 'sitemap'] as const
  const monitorType: MonitorType = EDIT_TYPES.includes(
    monitor.monitor_type as (typeof EDIT_TYPES)[number],
  )
    ? (monitor.monitor_type as MonitorType)
    : 'http'

  const form = useForm({
    defaultValues: {
      name: monitor.name,
      url: monitor.url,
      monitor_type: monitorType,
      http_method: currentMethod as HttpMethod,
      request_headers: headersToString(currentHeaders),
      request_body: currentBody,
      // The shared schema requires both. Without them every submit fails
      // validation and "Save Changes" does nothing. The edit request does not send them.
      verify_ssl: (settings.verify_ssl as boolean | undefined) ?? true,
      keyword: (settings.keyword as string | undefined) ?? '',
      dns_record_type: (settings.dns_record_type as string) || (settings.record_type as string) || 'A',
      dns_expected_value: (settings.dns_expected_value as string) || '',
      dns_server: (settings.dns_server as string) || '',
      ssl_expiry_threshold: (settings.expiry_threshold as number) || (settings.warn_days_before_expiry as number) || 30,
      domain_warn_days: (settings.warn_days as number) || 30,
      port_scan_ports: formatPortsSpec(Array.isArray(settings.ports) ? (settings.ports as PortSpec[]) : []),
      dns_record_expected: formatRecordList(settings.expected),
      blocklist_zones: formatRecordList(settings.zones),
      sitemap_sample: (settings.sample as number) || 20,
      browser_script: (settings.script as string) || '',
      browser_engine: (((settings.engine as string) || 'chromium') as BrowserEngine),
      visual_regression: (settings.visual_regression as boolean) ?? false,
      visual_threshold: (settings.visual_threshold as number) ?? 0.97,
      check_interval: monitor.interval,
      timeout: monitor.timeout,
      confirmation_threshold: monitor.confirmation_threshold ?? 2,
      response_time_threshold: (settings.response_time_threshold as number) || 0,
      escalation_policy_id: monitor.escalation_policy_id ?? '',
      reminder_interval_minutes: monitor.reminder_interval_minutes ?? null,
    },
    validators: {
      onSubmit: editMonitorSchema,
    },
    onSubmit: async ({ value }) => {
      const newSettings: Record<string, unknown> = {}

      if (value.monitor_type === 'http') {
        newSettings.method = value.http_method
        // The server merges settings key by key: a key left out keeps its old value. Send the
        // cleared state (no headers, no body) so removing a header or the body takes effect.
        newSettings.headers = parseHeadersString(value.request_headers)
        // The body field only shows for POST, PUT and PATCH: a hidden old body is not sent.
        newSettings.body =
          ['POST', 'PUT', 'PATCH'].includes(value.http_method) && value.request_body.trim() ? value.request_body : null
      } else if (value.monitor_type === 'dns') {
        newSettings.dns_record_type = value.dns_record_type
        if (value.dns_expected_value.trim()) newSettings.dns_expected_value = value.dns_expected_value
        if (value.dns_server.trim()) newSettings.dns_server = value.dns_server
      } else if (value.monitor_type === 'ssl') {
        newSettings.expiry_threshold = value.ssl_expiry_threshold
      } else if (value.monitor_type === 'domain') {
        newSettings.warn_days = value.domain_warn_days
      } else if (value.monitor_type === 'port_scan') {
        newSettings.ports = parsePortsSpec(value.port_scan_ports)
      } else if (value.monitor_type === 'dns_record') {
        newSettings.record_type = value.dns_record_type
        newSettings.expected = parseRecordList(value.dns_record_expected)
      } else if (value.monitor_type === 'blocklist') {
        const zones = parseRecordList(value.blocklist_zones)
        if (zones.length > 0) newSettings.zones = zones
      } else if (value.monitor_type === 'sitemap') {
        newSettings.sample = value.sitemap_sample
      } else if (value.monitor_type === 'browser') {
        Object.assign(newSettings, buildBrowserSettings(value))
        // Preserve baseline state the form doesn't edit (set by the probe / the
        // accept-baseline + ignore-regions actions) so an edit can't wipe it.
        if (settings.visual_baseline_version !== undefined) newSettings.visual_baseline_version = settings.visual_baseline_version
        if (settings.visual_ignore_regions !== undefined) newSettings.visual_ignore_regions = settings.visual_ignore_regions
      }

      if (value.response_time_threshold > 0) {
        newSettings.response_time_threshold = value.response_time_threshold
      }

      // Same merge rule: an emptied area list is sent when the monitor had areas, or they stay.
      if (!freeMonitor && areaChoice) {
        if (edgeInfo && !hasEnoughAreas(value.monitor_type, edgeInfo.areas, areaChoice)) {
          toast.error(`Pick at least ${MIN_AREAS} check areas.`)
          return
        }
        Object.assign(newSettings, edgeAreaSettings(value.monitor_type, areaChoice.edgeAreas, savedAreas(settings).length > 0))
      }

      // Same merge rule: an empty list must be sent, or the removed assertions stay on the monitor.
      if (assertions.length > 0 || Array.isArray(settings.assertions)) {
        newSettings.assertions = assertions.filter((a) => a.target || a.value)
      }

      updateMutation.mutate({
        name: value.name.trim(),
        url: value.url.trim(),
        interval: value.check_interval,
        timeout: value.timeout,
        confirmation_threshold: value.confirmation_threshold,
        reminder_interval_minutes: value.reminder_interval_minutes,
        escalation_policy_id: value.escalation_policy_id || null,
        settings: newSettings,
        ...(!freeMonitor && areaChoice && { region_ids: areaChoice.regionIds }),
        // [] means all channels. Ids of deleted channels are dropped, but only once the list is known.
        alert_contacts:
          chosen === null
            ? []
            : alertChannels.length > 0
              ? chosen.filter((id) => alertChannels.some((ch) => ch.id === id))
              : chosen,
      })
    },
  })

  const isPending = updateMutation.isPending

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Edit Monitor</h1>
          <p className="text-muted-foreground">Update {monitor.name}</p>
        </div>
        <Button variant="outline" asChild className="w-full sm:w-auto">
          <Link to="/dashboard/monitors/$id" params={{ id: monitor.id }}>Cancel</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Monitor Details</CardTitle>
          <CardDescription>Update the endpoint configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-4"
          >
            <form.Field name="name">
              {(field) => (
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    aria-label="Name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={isPending}
                  />
                  {field.state.meta.errors?.[0] && (
                    <p className="text-sm text-destructive">
                      {typeof field.state.meta.errors[0] === 'string'
                        ? field.state.meta.errors[0]
                        : getFieldError(field.state.meta.errors[0])}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) => (
                <form.Field name="url">
                  {(field) => (
                    <div className="space-y-2">
                      <Label>{mt === 'http' ? 'URL' : 'Domain'}</Label>
                      <Input
                        aria-label={mt === 'http' ? 'URL' : 'Domain'}
                        placeholder={mt === 'http' ? 'https://api.example.com/health' : 'example.com'}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                      />
                      {field.state.meta.errors?.[0] && (
                        <p className="text-sm text-destructive">
                          {typeof field.state.meta.errors[0] === 'string'
                            ? field.state.meta.errors[0]
                            : getFieldError(field.state.meta.errors[0])}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              )}
            </form.Subscribe>

            {/* HTTP-specific fields */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'http' ? (
                  <>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <form.Field name="http_method">
                        {(field) => (
                          <div className="space-y-2">
                            <Label>HTTP Method</Label>
                            <Select
                              value={field.state.value}
                              onValueChange={(v) => field.handleChange(v as HttpMethod)}
                              disabled={isPending}
                            >
                              <SelectTrigger aria-label="HTTP Method">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {HTTP_METHODS.map((m) => (
                                  <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </form.Field>

                      <form.Field name="check_interval">
                        {(field) => (
                          <div className="space-y-2">
                            <Label>Check Interval</Label>
                            <Select
                              value={String(field.state.value)}
                              onValueChange={(v) => field.handleChange(Number(v))}
                              disabled={isPending}
                            >
                              <SelectTrigger aria-label="Check Interval">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {checkIntervalOptions(30, monitor.interval).map((o) => {
                                  const full = o.value === 30 && fastBlocked
                                  return (
                                    <SelectItem key={o.value} value={String(o.value)} disabled={full}>
                                      {o.label}{full ? ' (slots full)' : ''}
                                    </SelectItem>
                                  )
                                })}
                              </SelectContent>
                            </Select>
                            {freeMonitor && (
                              <p className="text-sm text-muted-foreground">
                                Free monitors check from {freePlan.locationLabel}. Pro lets you choose the check locations.
                              </p>
                            )}
                            {Number.isFinite(slotLimit) && (
                              <p className="text-xs text-muted-foreground">
                                {usedFastSlots}/{slotLimit} 30-second slots used without a plan. Choose a plan for 30-second checks on every monitor.
                              </p>
                            )}
                          </div>
                        )}
                      </form.Field>
                    </div>

                    {showAreas && edgeInfo && selection && (
                      <AreaList info={edgeInfo} value={selection} onChange={setAreaChoice} disabled={isPending} />
                    )}

                    <form.Field name="request_headers">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Custom Headers</Label>
                          <textarea
                            aria-label="Custom Headers"
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder={"Authorization: Bearer token123\nContent-Type: application/json"}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          <p className="text-xs text-muted-foreground">
                            One header per line in Key: Value format
                          </p>
                        </div>
                      )}
                    </form.Field>

                    <form.Subscribe selector={(state) => state.values.http_method}>
                      {(method) =>
                        ['POST', 'PUT', 'PATCH'].includes(method) ? (
                          <form.Field name="request_body">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Request Body</Label>
                                <textarea
                                  aria-label="Request Body"
                                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  placeholder='{"key": "value"}'
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  onBlur={field.handleBlur}
                                  disabled={isPending}
                                />
                              </div>
                            )}
                          </form.Field>
                        ) : null
                      }
                    </form.Subscribe>
                  </>
                ) : null
              }
            </form.Subscribe>

            {/* DNS-specific fields */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'dns' ? (
                  <>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <form.Field name="dns_record_type">
                        {(field) => (
                          <div className="space-y-2">
                            <Label>Record Type</Label>
                            <Select
                              value={field.state.value}
                              onValueChange={field.handleChange}
                              disabled={isPending}
                            >
                              <SelectTrigger aria-label="Record Type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DNS_RECORD_TYPES.map((t) => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </form.Field>

                      <form.Field name="check_interval">
                        {(field) => (
                          <div className="space-y-2">
                            <Label>Check Interval</Label>
                            <Select
                              value={String(field.state.value)}
                              onValueChange={(v) => field.handleChange(Number(v))}
                              disabled={isPending}
                            >
                              <SelectTrigger aria-label="Check Interval">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {checkIntervalOptions(60, monitor.interval).map((o) => (
                                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </form.Field>
                    </div>

                    <form.Subscribe selector={(state) => ({ recordType: state.values.dns_record_type, expectedValue: state.values.dns_expected_value })}>
                      {({ recordType, expectedValue }) => {
                        const validator = DNS_VALUE_VALIDATORS[recordType]
                        const warning = expectedValue.trim() && validator ? validator(expectedValue.trim()) : null
                        return (
                          <form.Field name="dns_expected_value">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Expected Value</Label>
                                <Input
                                  aria-label="Expected Value"
                                  placeholder={DNS_PLACEHOLDER_HINTS[recordType] ?? '93.184.216.34'}
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  onBlur={field.handleBlur}
                                  disabled={isPending}
                                />
                                {warning ? (
                                  <p className="text-xs text-status-degraded">{warning}</p>
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    Leave empty to just verify the record exists
                                  </p>
                                )}
                              </div>
                            )}
                          </form.Field>
                        )
                      }}
                    </form.Subscribe>

                    <form.Field name="dns_server">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>DNS Server</Label>
                          <Input
                            aria-label="DNS Server"
                            placeholder="8.8.8.8 (leave empty for system default)"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                        </div>
                      )}
                    </form.Field>
                  </>
                ) : null
              }
            </form.Subscribe>

            {/* Browser-specific fields (script / engine / visual regression) */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'browser' ? (
                  <div className="space-y-4">
                    <BrowserFields form={form} isPending={isPending} />
                  </div>
                ) : null
              }
            </form.Subscribe>

            {/* SSL-specific fields */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'ssl' ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <form.Field name="ssl_expiry_threshold">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Expiry Alert Threshold</Label>
                          <Select
                            value={String(field.state.value)}
                            onValueChange={(v) => field.handleChange(Number(v))}
                            disabled={isPending}
                          >
                            <SelectTrigger aria-label="Expiry Alert Threshold">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">7 days</SelectItem>
                              <SelectItem value="14">14 days</SelectItem>
                              <SelectItem value="30">30 days</SelectItem>
                              <SelectItem value="60">60 days</SelectItem>
                              <SelectItem value="90">90 days</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Alert when certificate expires within this many days
                          </p>
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="check_interval">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Check Interval</Label>
                          <Select
                            value={String(field.state.value)}
                            onValueChange={(v) => field.handleChange(Number(v))}
                            disabled={isPending}
                          >
                            <SelectTrigger aria-label="Check Interval">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3600">1 hour</SelectItem>
                              <SelectItem value="21600">6 hours</SelectItem>
                              <SelectItem value="43200">12 hours</SelectItem>
                              <SelectItem value="86400">24 hours</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </form.Field>
                  </div>
                ) : null
              }
            </form.Subscribe>

            {/* Domain-expiry-specific field */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'domain' ? (
                  <form.Field name="domain_warn_days">
                    {(field) => (
                      <div className="space-y-2">
                        <Label>Expiry Alert Threshold</Label>
                        <Select
                          value={String(field.state.value)}
                          onValueChange={(v) => field.handleChange(Number(v))}
                          disabled={isPending}
                        >
                          <SelectTrigger aria-label="Expiry Alert Threshold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="14">14 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="60">60 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Alert when the domain registration expires within this many days
                        </p>
                      </div>
                    )}
                  </form.Field>
                ) : null
              }
            </form.Subscribe>

            {/* Port-scan-specific field */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'port_scan' ? (
                  <form.Field name="port_scan_ports">
                    {(field) => (
                      <div className="space-y-2">
                        <Label>Ports</Label>
                        <Input
                          aria-label="Ports"
                          placeholder="443, 80, 22:closed"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          disabled={isPending}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated ports; each expected open unless suffixed <code className="font-mono">:closed</code>.
                        </p>
                      </div>
                    )}
                  </form.Field>
                ) : null
              }
            </form.Subscribe>

            {/* DNS-record-change-specific fields */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'dns_record' ? (
                  <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
                    <form.Field name="dns_record_type">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Record type</Label>
                          <Select value={field.state.value} onValueChange={field.handleChange} disabled={isPending}>
                            <SelectTrigger aria-label="Record type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DNS_RECORD_CHANGE_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </form.Field>
                    <form.Field
                      name="dns_record_expected"
                      validators={{
                        onSubmit: ({ value }) =>
                          parseRecordList(value).length === 0 ? 'Add at least one expected record' : undefined,
                      }}
                    >
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Expected records</Label>
                          <Input
                            aria-label="Expected records"
                            placeholder="93.184.215.14, 93.184.215.15"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          {field.state.meta.errors?.[0] && (
                            <p className="text-sm text-destructive">{String(field.state.meta.errors[0])}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Comma-separated. Down when the resolved set differs in any way.
                          </p>
                        </div>
                      )}
                    </form.Field>
                  </div>
                ) : null
              }
            </form.Subscribe>

            {/* Sitemap-specific field */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'sitemap' ? (
                    <form.Field name="sitemap_sample">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>URLs to check per run</Label>
                          <Input
                            aria-label="URLs to check per run"
                            type="number"
                            min="1"
                            max="50"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(Number(e.target.value))}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          <p className="text-xs text-muted-foreground">
                            Point the URL at your site or its sitemap (a site root checks <code className="font-mono">/sitemap.xml</code>). Each run requests this many sitemap URLs, spread across the list, and is down if any returns 4xx/5xx.
                          </p>
                        </div>
                      )}
                    </form.Field>
                ) : null
              }
            </form.Subscribe>

            {/* DNS-blocklist-specific field */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(mt) =>
                mt === 'blocklist' ? (
                    <form.Field name="blocklist_zones">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Blocklist zones</Label>
                          <Input
                            aria-label="Blocklist zones"
                            placeholder="bl.spamcop.net, b.barracudacentral.org"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          <p className="text-xs text-muted-foreground">
                            Comma-separated DNSBL zones. Leave blank for the defaults (Spamhaus ZEN, SpamCop, Barracuda). Down when the host&rsquo;s IP is listed on any of them.
                          </p>
                        </div>
                      )}
                    </form.Field>
                ) : null
              }
            </form.Subscribe>

            <form.Field name="timeout">
              {(field) => (
                <div className="space-y-2">
                  <Label>Timeout (seconds)</Label>
                  <Input
                    aria-label="Timeout (seconds)"
                    type="number"
                    min="5"
                    max={timeoutMax({ freeTier: freeMonitor, editing: true })}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    onBlur={field.handleBlur}
                    disabled={isPending}
                  />
                  {freeMonitor && (
                    <p className="text-xs text-muted-foreground">The free plan allows up to 30 seconds.</p>
                  )}
                  {field.state.meta.errors?.[0] && (
                    <p className="text-sm text-destructive">
                      {typeof field.state.meta.errors[0] === 'string'
                        ? field.state.meta.errors[0]
                        : getFieldError(field.state.meta.errors[0])}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="confirmation_threshold">
              {(field) => (
                <div className="space-y-2">
                  <Label>Alert Confirmation</Label>
                  <Select
                    value={String(field.state.value)}
                    onValueChange={(v) => field.handleChange(Number(v))}
                    disabled={isPending}
                  >
                    <SelectTrigger aria-label="Alert Confirmation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 check (immediate)</SelectItem>
                      <SelectItem value="2">2 consecutive checks</SelectItem>
                      <SelectItem value="3">3 consecutive checks</SelectItem>
                      <SelectItem value="4">4 consecutive checks</SelectItem>
                      <SelectItem value="5">5 consecutive checks</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    How many consecutive failed checks before creating an incident. Higher = fewer false alerts.
                  </p>
                </div>
              )}
            </form.Field>

            <form.Field name="reminder_interval_minutes">
              {(field) => (
                <div className="space-y-2">
                  <Label>Still-Down Reminders</Label>
                  <Select
                    value={field.state.value === null ? 'off' : String(field.state.value)}
                    onValueChange={(v) =>
                      field.handleChange(v === 'off' ? null : Number(v))
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger aria-label="Still-Down Reminders">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every hour</SelectItem>
                      <SelectItem value="180">Every 3 hours</SelectItem>
                      <SelectItem value="360">Every 6 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Get reminded via your alert channels while a monitor stays down. Respects quiet hours. Stops when the monitor recovers or the incident is acknowledged, and after 5 reminders or 24 hours.
                  </p>
                </div>
              )}
            </form.Field>

            <form.Field name="response_time_threshold">
              {(field) => (
                <div className="space-y-2">
                  <Label>Response Time Alert (ms)</Label>
                  <Input
                    aria-label="Response Time Alert (ms)"
                    type="number"
                    min="0"
                    max="30000"
                    placeholder="0 (disabled)"
                    value={field.state.value || ''}
                    onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                    onBlur={field.handleBlur}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    Create an incident when response time exceeds this threshold. Set to 0 to disable.
                  </p>
                </div>
              )}
            </form.Field>

            {policies.length > 0 && (
              <form.Field name="escalation_policy_id">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Escalation Policy</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      disabled={isPending}
                    >
                      <SelectTrigger aria-label="Escalation Policy">
                        <SelectValue placeholder="No escalation policy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None (alert all channels)</SelectItem>
                        {policies.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.steps.length} steps)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Escalate alerts through channels in sequence instead of all at once
                    </p>
                  </div>
                )}
              </form.Field>
            )}

            {/* Alert Channels */}
            {alertChannels.length > 0 && (
              <div className="space-y-3">
                <Label>Alert Channels</Label>
                <p className="text-sm text-muted-foreground">
                  Select which channels receive alerts for this monitor. All channels are selected by default.
                </p>
                <div className="space-y-2">
                  {alertChannels.map((channel) => {
                    const isChecked = isChannelChecked(channel.id)

                    return (
                      <label
                        key={channel.id}
                        className="flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <Checkbox
                          aria-label={channel.name ?? `${channel.type} channel`}
                          checked={isChecked}
                          onCheckedChange={(checked) => toggleChannel(channel.id, !!checked)}
                          disabled={isPending}
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">{channel.name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{channel.type}</span>
                        </div>
                      </label>
                    )
                  })}
                </div>
                {noChannelChecked && (
                  <p role="alert" className="text-sm text-status-down">
                    Pick at least one alert channel. A monitor with none would alert all of them.
                  </p>
                )}
              </div>
            )}

            {/* Response Assertions */}
            {monitorType === 'http' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Response Assertions</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAssertions([...assertions, { type: 'json_path', target: '', operator: 'eq', value: '' }])}
                    disabled={isPending}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add
                  </Button>
                </div>
                {assertions.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Validate response body, headers, or status code. All assertions must pass for the check to be "up".
                  </p>
                )}

                {!assertions.some(a => a.type === 'status_code') && (
                  <p className="text-xs text-muted-foreground">
                    Default: 2xx = up, anything else = down.
                  </p>
                )}
                {assertions.map((assertion, idx) => (
                  <div key={idx} className="flex flex-wrap items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
                    <Select
                      value={assertion.type}
                      onValueChange={(v) => {
                        const next = [...assertions]
                        next[idx] = { ...next[idx], type: v as Assertion['type'] }
                        setAssertions(next)
                      }}
                      disabled={isPending}
                    >
                      <SelectTrigger aria-label={`Assertion ${idx + 1} type`} className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSERTION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {!['contains', 'not_contains', 'status_code'].includes(assertion.type) && (
                      <Input
                        aria-label={`Assertion ${idx + 1} target`}
                        className="w-[160px]"
                        placeholder={
                          assertion.type === 'json_path' ? '$.data.status'
                            : assertion.type === 'header' ? 'content-type'
                            : assertion.type === 'regex' ? 'pattern'
                            : 'target'
                        }
                        value={assertion.target}
                        onChange={(e) => {
                          const next = [...assertions]
                          next[idx] = { ...next[idx], target: e.target.value }
                          setAssertions(next)
                        }}
                        disabled={isPending}
                      />
                    )}

                    {['json_path', 'header', 'status_code'].includes(assertion.type) && (
                      <Select
                        value={assertion.operator}
                        onValueChange={(v) => {
                          const next = [...assertions]
                          next[idx] = { ...next[idx], operator: v }
                          setAssertions(next)
                        }}
                        disabled={isPending}
                      >
                        <SelectTrigger aria-label={`Assertion ${idx + 1} operator`} className="w-[90px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {assertion.type !== 'regex' && (
                      <Input
                        aria-label={`Assertion ${idx + 1} expected value`}
                        className="flex-1 min-w-[120px]"
                        placeholder={
                          assertion.type === 'status_code' ? '200'
                            : assertion.type === 'contains' || assertion.type === 'not_contains' ? 'keyword'
                            : 'expected value'
                        }
                        value={assertion.type === 'contains' || assertion.type === 'not_contains' ? assertion.target : assertion.value}
                        onChange={(e) => {
                          const next = [...assertions]
                          if (assertion.type === 'contains' || assertion.type === 'not_contains') {
                            next[idx] = { ...next[idx], target: e.target.value }
                          } else {
                            next[idx] = { ...next[idx], value: e.target.value }
                          }
                          setAssertions(next)
                        }}
                        disabled={isPending}
                      />
                    )}

                    {assertion.type === 'regex' && (
                      <Input
                        aria-label={`Assertion ${idx + 1} pattern`}
                        className="flex-1 min-w-[120px] font-mono"
                        placeholder='version":\s*"\d+'
                        value={assertion.target}
                        onChange={(e) => {
                          const next = [...assertions]
                          next[idx] = { ...next[idx], target: e.target.value }
                          setAssertions(next)
                        }}
                        disabled={isPending}
                      />
                    )}

                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={`Remove assertion ${idx + 1}`}
                      size="sm"
                      onClick={() => setAssertions(assertions.filter((_, i) => i !== idx))}
                      disabled={isPending}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={isPending || noChannelChecked}>
                {isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: '/dashboard/monitors/$id', params: { id: monitor.id } })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
